import type { ScoringRules, UserAnswer, Question } from '@examflow/core';
import { TCFLevel, type TCFScoreMapping, type TCFResult } from './types';

/**
 * TCF 分数与等级对照表
 * 基于官方 TCF 评分标准
 */
export const TCF_SCORE_MAPPINGS: TCFScoreMapping[] = [
  {
    level: TCFLevel.A1,
    minScore: 100,
    maxScore: 199,
    cefrEquivalent: 'A1',
    description: '初级入门 - 能够理解和使用日常用语',
  },
  {
    level: TCFLevel.A2,
    minScore: 200,
    maxScore: 299,
    cefrEquivalent: 'A2',
    description: '初级 - 能够进行简单的日常交流',
  },
  {
    level: TCFLevel.B1,
    minScore: 300,
    maxScore: 399,
    cefrEquivalent: 'B1',
    description: '中级 - 能够应对日常生活中的大多数情况',
  },
  {
    level: TCFLevel.B2,
    minScore: 400,
    maxScore: 499,
    cefrEquivalent: 'B2',
    description: '中高级 - 能够流利地进行交流，理解复杂文本',
  },
  {
    level: TCFLevel.C1,
    minScore: 500,
    maxScore: 599,
    cefrEquivalent: 'C1',
    description: '高级 - 能够自如地表达，理解要求较高的长篇文章',
  },
  {
    level: TCFLevel.C2,
    minScore: 600,
    maxScore: 699,
    cefrEquivalent: 'C2',
    description: '精通级 - 能够轻松理解所有内容，接近母语水平',
  },
];

/**
 * 根据分数获取 TCF 等级
 */
export function getTCFLevel(score: number): TCFLevel {
  for (const mapping of TCF_SCORE_MAPPINGS) {
    if (score >= mapping.minScore && score <= mapping.maxScore) {
      return mapping.level;
    }
  }
  // 低于 A1 标准
  if (score < 100) return TCFLevel.A1;
  // 超过 C2 标准（理论上不会发生）
  return TCFLevel.C2;
}

/**
 * 获取等级详细信息
 */
export function getLevelInfo(level: TCFLevel): TCFScoreMapping | undefined {
  return TCF_SCORE_MAPPINGS.find((m) => m.level === level);
}

/**
 * TCF 评分规则
 */
export const tcfScoringRules: ScoringRules = {
  totalScore: 699,
  passingScore: 200, // A2 等级起步
  calculateScore: (answers: UserAnswer[], questions: Question[]): number => {
    // TCF 使用 IRT（项目反应理论）评分
    // 这里使用简化版本：根据正确率和题目难度计算

    if (questions.length === 0) return 0;

    const questionMap = new Map(questions.map((q) => [q.id, q]));
    let weightedScore = 0;
    let totalWeight = 0;

    // 难度权重（基于 CEFR 等级）
    const difficultyWeights = {
      A1: 1,
      A2: 1.2,
      B1: 1.5,
      B2: 1.8,
    } as const;

    for (const answer of answers) {
      const question = questionMap.get(answer.questionId);
      if (!question) continue;

      // 使用 level 字段（A1/A2/B1/B2），如果未设置则使用默认值 A2
      const level = question.level;
      let normalizedDifficulty: keyof typeof difficultyWeights = 'A2';
      if (level && level in difficultyWeights) {
        normalizedDifficulty = level as keyof typeof difficultyWeights;
      }

      const weight = difficultyWeights[normalizedDifficulty];
      totalWeight += weight;

      if (answer.isCorrect) {
        weightedScore += weight;
      }
    }

    if (totalWeight === 0) return 0;

    // 将加权正确率映射到 TCF 分数范围 (100-699)
    const accuracy = weightedScore / totalWeight;
    const score = Math.round(100 + accuracy * 599);

    return Math.min(699, Math.max(100, score));
  },
};

/**
 * 计算完整的 TCF 结果
 */
export function calculateTCFResult(
  answers: UserAnswer[],
  questions: Question[]
): TCFResult {
  const score = tcfScoringRules.calculateScore(answers, questions);
  const level = getTCFLevel(score);
  const levelInfo = getLevelInfo(level);

  // 按部分计算分数（简化版）
  const sectionScores = {
    listening: 0,
    language_structures: 0,
    reading: 0,
    speaking: 0,
    writing: 0,
  };

  return {
    totalScore: score,
    level,
    sectionScores,
    cefrLevel: levelInfo?.cefrEquivalent || 'A1',
    evaluation: levelInfo?.description || '继续努力！',
  };
}
